import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import type { ScreenBlock, ScreenBlockType } from "@ncp/screens";
import type { BaseBody } from "../generic/api";

export type ScreenSurface = "backoffice" | "portal";
export const SCREEN_KINDS = ["list", "detail", "dashboard", "form"] as const;

export interface ScreenBody extends BaseBody {
  surface: ScreenSurface;
  kind: (typeof SCREEN_KINDS)[number];
  recordTypeKey?: string | null;
  serviceKey?: string | null;
  blocks: ScreenBlock[];
  showInMenu: boolean;
  menuIcon: string;
  menuOrder: number;
  departmentKey?: string | null;
  roleKeys: string[];
}

export interface ScreenRef { key: string; name: string; nameAr?: string | null; published: boolean; portal?: boolean }
export interface ScreenOptions { recordTypes: ScreenRef[]; reports: ScreenRef[]; services: ScreenRef[]; roles: Array<{ key: string; name: string; nameAr?: string | null }>; blocks: ScreenBlockType[]; portalBlocks: ScreenBlockType[] }

export function useScreenOptions() {
  const api = useApi();
  return useQuery({ queryKey: ["screens", "options"], queryFn: () => api.get<ScreenOptions>("/api/platform/screens/options"), staleTime: 30_000 });
}

/** A starting layout per kind, so a new screen previews something useful straight away. */
export function starterBlocks(kind: ScreenBody["kind"], surface: ScreenSurface, name: string, ref: { recordType?: string; service?: string }): ScreenBlock[] {
  const heading: ScreenBlock = { type: "heading", text: name };
  if (surface === "portal") return ref.service ? [heading, { type: "serviceStart", service: ref.service }, { type: "caseList", service: ref.service }] : [heading];
  switch (kind) {
    case "list": return ref.recordType ? [heading, { type: "recordList", recordType: ref.recordType, allowCreate: true }, { type: "recordDetail", recordType: ref.recordType, showLinks: true }]
      : ref.service ? [heading, { type: "caseList", service: ref.service }, { type: "caseDetail" }] : [heading];
    case "detail": return ref.service ? [heading, { type: "caseList", service: ref.service }, { type: "caseDetail" }] : ref.recordType ? [heading, { type: "recordList", recordType: ref.recordType }, { type: "recordDetail", recordType: ref.recordType }] : [heading];
    case "form": return ref.service ? [heading, { type: "serviceStart", service: ref.service }] : ref.recordType ? [heading, { type: "recordForm", recordType: ref.recordType }] : [heading];
    default: return [heading];
  }
}

export function newScreenBody(name: string, nameAr: string | null, extra: Record<string, string>): ScreenBody {
  const surface = (extra.surface as ScreenSurface) || "backoffice";
  const kind = (extra.kind as ScreenBody["kind"]) || (surface === "portal" ? "form" : "list");
  return {
    name, nameAr, description: null, descriptionAr: null, tags: [],
    surface, kind, recordTypeKey: extra.recordType || null, serviceKey: extra.service || null,
    blocks: starterBlocks(kind, surface, name, { recordType: extra.recordType || undefined, service: extra.service || undefined }),
    showInMenu: true, menuIcon: surface === "portal" ? "FileText" : "LayoutList", menuOrder: 100, departmentKey: null, roleKeys: [],
  };
}

/** A fresh block of a type, with the references filled from the screen's own record type or service. */
export function blankBlock(type: ScreenBlockType, body: ScreenBody, options?: ScreenOptions): ScreenBlock {
  const recordType = body.recordTypeKey || options?.recordTypes[0]?.key || "";
  const service = body.serviceKey || options?.services[0]?.key || "";
  const report = options?.reports[0]?.key ?? "";
  switch (type) {
    case "heading": return { type, text: body.name };
    case "text": return { type, text: "" };
    case "recordList": return { type, recordType, allowCreate: true };
    case "recordForm": return { type, recordType };
    case "recordDetail": return { type, recordType, showLinks: true };
    case "report": return { type, report };
    case "kpi": return { type, report };
    case "serviceStart": return { type, service };
    case "caseList": return { type, service };
    case "caseDetail": return { type };
  }
}
